import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  HStack,
  Stack,
  Button,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  getLibrariesData,
  updateLibrariesData,
} from "../../../services/Libraries";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData, transformData } from "../../../util/helper";
import ImageWidget from "../../../CommonComponents/ImageWidget";
import ImagePreview from "../../../components/ImagePreview";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import { handleApiError, showSuccessToast } from "../../../util/errorHandler";

// Renders nothing; keeps the field value in formData (ui:widget "hidden" does not apply to array fields)
const HiddenField = () => null;

const Section4 = () => {
  const { alias } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();
  const type = window.location.pathname.split("/")[1];

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "creator_first_name",
      "creator_surname",
      "creator_name_alias",
      "contributors1",
      "contributors1_type",
      "contributors1_alias",
      "contributors2",
      "contributors2_type",
      "contributors2_alias",
      "contributors3",
      "contributors3_type",
      "contributors3_alias",
      "contributors4",
      "contributors4_type",
      "contributors4_alias",
      "whether_book_is_a_masterlist",
      "featured_artist",
      "pdf_scan_of_contents",
      "pdf_scan_of_index",
      "online_reference_url",
      "personality_or_film_visually_featured_on_cover",
      "drawer_image_alias",
    ];
    const rightFields = [
      "quotations_and_other_text",
      "theme",
      "type_of_joint_creator",
      "scene_description",
      "backCover",
      "set_of",
      "is_source",
      "rec_aliases",
      "extended_tags",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    title: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      creator_first_name: {
        type: "string",
        title: "Creator first name",
      },
      creator_surname: {
        type: "string",
        title: "Creator surname",
      },
      creator_name_alias: {
        type: "string",
        title: "Creator name alias",
      },
      contributors1: {
        type: "string",
        title: "Contributors 1",
      },
      contributors1_type: {
        type: "string",
        title: "Contributors 1 type",
      },
      contributors1_alias: {
        type: "string",
        title: "Contributors 1 alias",
      },
      contributors2: {
        type: "string",
        title: "Contributors 2",
      },
      contributors2_type: {
        type: "string",
        title: "Contributors 2 type",
      },
      contributors2_alias: {
        type: "string",
        title: "Contributors 2 alias",
      },
      contributors3: {
        type: "string",
        title: "Contributors 3",
      },
      contributors3_type: {
        type: "string",
        title: "Contributors 3 type",
      },
      contributors3_alias: {
        type: "string",
        title: "Contributors 3 alias",
      },
      contributors4: {
        type: "string",
        title: "Contributors 4",
      },
      contributors4_type: {
        type: "string",
        title: "Contributors 4 type",
      },
      contributors4_alias: {
        type: "string",
        title: "Contributors 4 alias",
      },
      whether_book_is_a_masterlist: {
        type: "string",
        title: "Whether book is a masterlist",
      },
      featured_artist: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Featured artist",
      },
      pdf_scan_of_contents: {
        type: "string",
        title: "PDF scan of contents",
      },
      pdf_scan_of_index: {
        type: "string",
        title: "PDF scan of index",
      },
      online_reference_url: {
        type: "string",
        title: "Online reference URL",
      },
      personality_or_film_visually_featured_on_cover: {
        type: "string",
        title: "Personality or film visually featured on cover",
      },
      drawer_image_alias: {
        type: "array",
        title: "Drawer image alias",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
    },
  };

  // Right column schema
  const rightColumnSchema = {
    title: "",
    type: "object",
    properties: {
      quotations_and_other_text: {
        type: "string",
        title: "Quotations and other text",
      },
      theme: {
        type: "string",
        title: "Theme",
      },
      type_of_joint_creator: {
        type: "string",
        title: "Type of joint creator",
      },
      scene_description: {
        type: "string",
        title: "Scene description",
      },
      backCover: {
        type: "boolean",
        default: false,
        title: "Back cover",
      },
      set_of: {
        type: "string",
        title: "Set of",
      },
      is_source: {
        type: "string",
        default: "",
        title: "Is source",
      },
      rec_aliases: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Rec aliases",
      },
      extended_tags: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Extended tags",
      },
    },
  };

  // Combined schema for preview mode
  const combinedSchema = {
    title: "Quadrant-4 fields",
    description: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      creator_first_name: {
        type: "string",
        title: "Creator first name",
      },
      creator_surname: {
        type: "string",
        title: "Creator surname",
      },
      contributors1_alias: {
        type: "string",
        title: "Contributors 1 alias",
      },
      contributors2_alias: {
        type: "string",
        title: "Contributors 2 alias",
      },
      contributors1: {
        type: "string",
        title: "Contributors 1",
      },
      contributors1_type: {
        type: "string",
        title: "Contributors 1 type",
      },
      contributors2: {
        type: "string",
        title: "Contributors 2",
      },
      contributors2_type: {
        type: "string",
        title: "Contributors 2 type",
      },
      contributors3: {
        type: "string",
        title: "Contributors 3",
      },
      contributors3_type: {
        type: "string",
        title: "Contributors 3 type",
      },
      contributors3_alias: {
        type: "string",
        title: "Contributors 3 alias",
      },
      contributors4: {
        type: "string",
        title: "Contributors 4",
      },
      contributors4_type: {
        type: "string",
        title: "Contributors 4 type",
      },
      contributors4_alias: {
        type: "string",
        title: "Contributors 4 alias",
      },
      creator_name_alias: {
        type: "string",
        title: "Creator name alias",
      },
      whether_book_is_a_masterlist: {
        type: "string",
        title: "Whether book is a masterlist",
      },
      featured_artist: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Featured artist",
      },
      pdf_scan_of_contents: {
        type: "string",
        title: "PDF scan of contents",
      },
      pdf_scan_of_index: {
        type: "string",
        title: "PDF scan of index",
      },
      online_reference_url: {
        type: "string",
        title: "Online reference URL",
      },
      personality_or_film_visually_featured_on_cover: {
        type: "string",
        title: "Personality or film visually featured on cover",
      },
      drawer_image_alias: {
        type: "array",
        title: "Drawer image alias",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      quotations_and_other_text: {
        type: "string",
        title: "Quotations and other text",
      },
      theme: {
        type: "string",
        title: "Theme",
      },
      type_of_joint_creator: {
        type: "string",
        title: "Type of joint creator",
      },
      scene_description: {
        type: "string",
        title: "Scene description",
      },
      backCover: {
        type: "boolean",
        default: false,
        title: "Back cover",
      },
      set_of: {
        type: "string",
        title: "Set of",
      },
      is_source: {
        type: "string",
        default: "",
        title: "Is source",
      },
      rec_aliases: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Rec aliases",
      },
      extended_tags: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Extended tags",
      },
    },
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    creator_first_name: { "ui:emptyValue": "" },
    creator_surname: { "ui:emptyValue": "" },
    creator_name_alias: { "ui:emptyValue": "" },
    contributors1: { "ui:emptyValue": "" },
    contributors1_type: { "ui:emptyValue": "" },
    contributors1_alias: { "ui:emptyValue": "" },
    contributors2: { "ui:emptyValue": "" },
    contributors2_type: { "ui:emptyValue": "" },
    contributors2_alias: { "ui:emptyValue": "" },
    contributors3: { "ui:emptyValue": "" },
    contributors3_type: { "ui:emptyValue": "" },
    contributors3_alias: { "ui:emptyValue": "" },
    contributors4: { "ui:emptyValue": "" },
    contributors4_type: { "ui:emptyValue": "" },
    contributors4_alias: { "ui:emptyValue": "" },
    whether_book_is_a_masterlist: { "ui:widget": "hidden" },
    drawer_image_alias: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    featured_artist: {
      "ui:emptyValue": "",
      "ui:readonly": true,
      items: {
        "ui:emptyValue": "",
      },
    },
    pdf_scan_of_contents: { "ui:emptyValue": "" },
    pdf_scan_of_index: { "ui:emptyValue": "" },
    online_reference_url: { "ui:emptyValue": "" },
    personality_or_film_visually_featured_on_cover: { "ui:emptyValue": "" },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": {
      fontSize: "14px",
    },
    rec_aliases: { "ui:field": HiddenField },
    extended_tags: { "ui:field": HiddenField },
    is_source: { "ui:widget": "hidden" },
    quotations_and_other_text: { "ui:widget": "hidden" },
    theme: { "ui:widget": "hidden" },
    type_of_joint_creator: { "ui:emptyValue": "" },
    scene_description: {
      "ui:emptyValue": "",
      "ui:widget": "textarea",
    },
    set_of: { "ui:emptyValue": "" },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    rec_aliases: { "ui:field": HiddenField },
    extended_tags: { "ui:field": HiddenField },
    is_source: { "ui:widget": "hidden" },
    drawer_image_alias: {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    featured_artist: {
      "ui:emptyValue": "",
      "ui:readonly": true,
      items: {
        "ui:emptyValue": "",
      },
    },
    creator_first_name: { "ui:emptyValue": "" },
    creator_surname: { "ui:emptyValue": "" },
    contributors1_alias: { "ui:emptyValue": "" },
    contributors2_alias: { "ui:emptyValue": "" },
    contributors1: { "ui:emptyValue": "" },
    contributors1_type: { "ui:emptyValue": "" },
    contributors2: { "ui:emptyValue": "" },
    contributors2_type: { "ui:emptyValue": "" },
    contributors3: { "ui:emptyValue": "" },
    contributors3_type: { "ui:emptyValue": "" },
    contributors3_alias: { "ui:emptyValue": "" },
    contributors4: { "ui:emptyValue": "" },
    contributors4_type: { "ui:emptyValue": "" },
    contributors4_alias: { "ui:emptyValue": "" },
    creator_name_alias: { "ui:emptyValue": "" },
    whether_book_is_a_masterlist: { "ui:widget": "hidden" },
    pdf_scan_of_contents: { "ui:emptyValue": "" },
    pdf_scan_of_index: { "ui:emptyValue": "" },
    online_reference_url: { "ui:emptyValue": "" },
    personality_or_film_visually_featured_on_cover: { "ui:emptyValue": "" },
    quotations_and_other_text: { "ui:widget": "hidden" },
    theme: { "ui:widget": "hidden" },
    type_of_joint_creator: { "ui:emptyValue": "" },
    scene_description: {
      "ui:emptyValue": "",
      "ui:widget": "textarea",
    },
    set_of: { "ui:emptyValue": "" },
  };

  // Helper function to get first available image from drawer_image_alias array
  const getFirstDrawerImage = (formData) => {
    if (
      formData?.drawer_image_alias &&
      Array.isArray(formData.drawer_image_alias) &&
      formData.drawer_image_alias.length > 0
    ) {
      // Look for the first item with a valid image
      for (const item of formData.drawer_image_alias) {
        if (item?.image && item.image.trim() !== "") {
          return item.image;
        }
      }
    }
    return null;
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getLibrariesData(alias, "section-4", type);
          if (result) {
            const fieldsToTransform = ["drawer_image_alias"];

            const transformedData = transformData(
              result?.data?.[0],
              fieldsToTransform,
            );

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      const fieldsToTransform = ["drawer_image_alias"];

      const updatedFormData = reverseTransformData(
        finalFormData,
        fieldsToTransform,
      );

      if (alias) {
        const result = await updateLibrariesData(
          alias,
          updatedFormData,
          "",
          "section-4",
        );

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Library Quadrant-4 Fields
                  </Box>
                  <IconButton
                    aria-label={
                      showImagePreview
                        ? "Hide image preview"
                        : "Show image preview"
                    }
                    icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    colorScheme={showImagePreview ? "red" : "blue"}
                    onClick={() => setShowImagePreview((prev) => !prev)}
                    title={
                      showImagePreview
                        ? "Close image preview"
                        : "Open image preview"
                    }
                  />
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={getFirstDrawerImage(combinedFormData)} />
                </HStack>
              )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Library Quadrant-4 Fields
                      </Box>
                      <IconButton
                        aria-label={
                          showImagePreview
                            ? "Hide image preview"
                            : "Show image preview"
                        }
                        icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        colorScheme={showImagePreview ? "red" : "blue"}
                        onClick={() => setShowImagePreview((prev) => !prev)}
                        title={
                          showImagePreview
                            ? "Close image preview"
                            : "Open image preview"
                        }
                      />
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={getFirstDrawerImage(leftFormData)} />
                </HStack>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section4;
